import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { DatePicker } from '@/components/ui/DatePicker';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { ApiRequestError } from '@/lib/apiClient';
import type { Holiday, ParentSmsOutcome } from '@/types/holiday';
import { holidaysApi } from '../api/holidays.api';
import { HOLIDAY_TYPE_HINT, HOLIDAY_TYPE_OPTIONS } from '../constants';
import {
  EMPTY_HOLIDAY_FORM,
  holidayFormResolver,
  holidaySchema,
  type HolidayFormValues,
} from '../schemas/holiday.schema';
import { toFormValues } from '../utils';
import { applyFieldErrors } from '@/lib/forms';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/** Fixed so the checkbox and the sentence describing it can be tied together. */
const STAFF_WORKING_FIELD = 'holiday-staff-working';
const NOTIFY_PARENTS_FIELD = 'holiday-notify-parents';

export interface HolidayFormModalProps {
  /** The holiday being edited, or null to declare a new one. */
  holiday: Holiday | null;
  /** Fields a new declaration opens with — what was typed before switching here. */
  draft?: Partial<HolidayFormValues>;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Gets the saved holiday back, so the page can confirm it by name — and, when
   * the parents were texted, how that went.
   */
  onSaved: (saved: Holiday, wasCreated: boolean, parentSms: ParentSmsOutcome | null) => void;
}

/**
 * Declaring a holiday, and correcting one already declared.
 *
 * One dialog for both: the fields are identical, and a second component that
 * differed only in which API call it made would be the copy-paste CLAUDE.md §3
 * exists to prevent.
 */
export function HolidayFormModal({
  holiday,
  draft,
  isOpen,
  onClose,
  onSaved,
}: HolidayFormModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const isEditing = Boolean(holiday);
  // Texting every parent is the notifications screen's key, not the calendar's —
  // the server refuses without it, so the box is not offered either.
  const canSmsParents = useAuthStore(
    (state) => state.user?.permissions?.includes('MESSAGE_SEND') ?? false,
  );

  const form = useForm<HolidayFormValues>({
    resolver: holidayFormResolver,
    defaultValues: EMPTY_HOLIDAY_FORM,
  });
  useLiveFormErrors(form);
  const {
    formState: { isSubmitting, errors },
    register,
    setValue,
    watch,
  } = form;

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const selectedType = watch('type');

  // Refill whenever the dialog opens, so a half-typed declaration never appears
  // over the holiday opened next.
  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    form.reset(holiday ? toFormValues(holiday) : { ...EMPTY_HOLIDAY_FORM, ...draft });
  }, [isOpen, holiday, draft, form]);

  /**
   * Most holidays are one day, so the last day follows the first until somebody
   * says otherwise. Only ever moved forward — an end already after the start is
   * the institute's own answer and is left alone.
   */
  useEffect(() => {
    if (!startDate) return;
    if (!endDate || endDate < startDate) {
      setValue('endDate', startDate, { shouldValidate: Boolean(endDate) });
    }
  }, [startDate, endDate, setValue]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);

    try {
      const payload = holidaySchema.parse(values);

      // The page raises the confirmation once this dialog has closed — a toast
      // under an open `<dialog>` sits behind it, in the browser's top layer.
      if (holiday) {
        onSaved(await holidaysApi.update(holiday.uuid, payload), false, null);
      } else {
        const declared = await holidaysApi.create(
          payload,
          canSmsParents && values.notifyParentsBySms,
        );
        onSaved(declared, true, declared.parentSms);
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // Field errors stay on the fields — a message that fades cannot be the
        // only place an institute is told which box to fix.
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit ${holiday?.name}` : 'Declare a holiday'}
      description={
        isEditing
          ? 'Moving the dates changes which days the registers treat as closed.'
          : 'The institute is shown as closed on these days, and nobody is marked absent on them.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} isLoading={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEditing ? 'Save changes' : 'Declare holiday'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this holiday">
            {formError}
          </Alert>
        )}

        <Input
          label="Name"
          placeholder="Dussehra, Founder's Day, Summer Vacation"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <DatePicker
            label="First day"
            error={errors.startDate?.message}
            {...register('startDate')}
          />
          <DatePicker
            label="Last day"
            hint="Same as the first day for a single-day holiday."
            error={errors.endDate?.message}
            {...register('endDate')}
          />
        </div>

        <Select
          label="Kind of holiday"
          options={HOLIDAY_TYPE_OPTIONS}
          hint={selectedType ? HOLIDAY_TYPE_HINT[selectedType] : undefined}
          error={errors.type?.message}
          {...register('type')}
        />

        <Textarea
          label="Note (optional)"
          rows={2}
          placeholder="Declared by the district collector"
          hint="Shown on the calendar, so the office knows why the institute is shut."
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="flex flex-col gap-1.5">
          <Checkbox
            id={STAFF_WORKING_FIELD}
            label="Staff still report to institute"
            aria-describedby={`${STAFF_WORKING_FIELD}-hint`}
            {...register('isStaffWorking')}
          />
          {/* `Checkbox` takes no hint of its own, so the explanation is a
              sibling tied to it by `aria-describedby` rather than a prop added
              to a component the whole product shares. */}
          <p id={`${STAFF_WORKING_FIELD}-hint`} className="pl-6 text-sm text-ink-2">
            For a vacation the children are off but teachers come in — for admissions, results or
            paper setting.
          </p>
        </div>

        {/* Declaring only: moving an existing holiday's dates is a correction,
            and a second text about the same days would be noise. */}
        {!isEditing && canSmsParents && (
          <div className="flex flex-col gap-1.5">
            <Checkbox
              id={NOTIFY_PARENTS_FIELD}
              label="Send an SMS to parents"
              aria-describedby={`${NOTIFY_PARENTS_FIELD}-hint`}
              {...register('notifyParentsBySms')}
            />
            <p id={`${NOTIFY_PARENTS_FIELD}-hint`} className="pl-6 text-sm text-ink-2">
              One text per family, to every enrolled student's parent or guardian: the holiday's
              name and kind, its dates, your note, and the day classes resume.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}
